import * as React from 'react';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';
import PrintIcon from '@mui/icons-material/Print';
import RoomIcon from '@mui/icons-material/Room';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import PersonIcon from '@mui/icons-material/Person';
import IconButton from '@mui/material/IconButton';
import styled from '@emotion/styled';
import { useSearchParams } from 'react-router-dom';
import StyledToolTips from '../../../../../../utils/tooltip/StyledToolTip';
import { PractitionerOrOrganization } from '../../../../../../interfaces/interfaces';
import OperationHours from './OperationHours';
import SuggestEdit from './SuggestEdit';

const StyledContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-content: center;
  margin: 0.3rem 0;
`;

const StyledContainerColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledIconDiv = styled.div`
  margin: auto 50px auto 5px;
`;

const StyledSpecialty = styled.div`
  margin-bottom: 15px;
`;

const StyledInfoContainer = styled.div`
  background-color: #ffffff;
  margin: 0 10px 0;
  padding: 0 10px 0;
  opacity: 0.9;
  position: absolute;
  left: calc(100% + 10px);
  height: 100%;
  top: 0;
  width: 100%;
  z-index: 1;
  overflow-y: auto;
`;

const StyledCloseButton = styled.div`
  display: flex;
  justify-content: end;
`;

const StyledDebugDiv = styled.div`
  color: red;
  font-size: 10px;
`;

const StyledWebsiteIcon = styled.a`
  color: #4b286d;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: #092c4c;
  }
`;

interface PractitionerInfoProps {
  value: PractitionerOrOrganization;

  setToggle(x: boolean): void;
}

const PractitionerInfo = ({ value, setToggle }: PractitionerInfoProps) => {
  const {
    id,
    name,
    orgType,
    pharmacyType,
    findPharmacyChain,
    telecom: { phone, fax, email, url },
    address: { fullAddress, locationSecondary, id: locationId },
    registrationProvince,
    billingNumbers,
    secondarySpecialties = [],
    specialty,
    language,
    gender,
    contact,
    catchmentArea,
    resourceType,
    operationHours,
  } = value;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [searchParams] = useSearchParams();

  return (
    <StyledInfoContainer>
      <StyledCloseButton>
        <IconButton
          onClick={() => {
            setToggle(false);
          }}
        >
          <CancelPresentationIcon color="disabled" />
        </IconButton>
      </StyledCloseButton>
      <h3>{name ?? '-'}</h3>
      {searchParams.get('d') === '1' && <StyledDebugDiv>ID: {id}</StyledDebugDiv>}
      {resourceType === 'Practitioner' && (
        <StyledSpecialty>{specialty !== '-' ? <h4>{specialty}</h4> : <span>Specialty info is not available</span>}</StyledSpecialty>
      )}
      {resourceType === 'Practitioner' && secondarySpecialties?.length > 0 && <StyledSpecialty>{secondarySpecialties.join(', ')}</StyledSpecialty>}
      {resourceType === 'Organization' && (
        <>
          <StyledSpecialty>{`${orgType ?? '-'}/ ${pharmacyType && pharmacyType !== 'n/a' ? pharmacyType : '-'}`}</StyledSpecialty>
          <StyledSpecialty>{`${findPharmacyChain && findPharmacyChain !== 'n/a' ? findPharmacyChain : '-'}`}</StyledSpecialty>
          <StyledContainer>
            <StyledIconDiv>
              <StyledToolTips title="Location" enterDelay={100} arrow>
                <RoomIcon color="disabled" />
              </StyledToolTips>
            </StyledIconDiv>
            <div>
              <div>{fullAddress ?? '-'}</div>
            </div>
          </StyledContainer>
          {!!operationHours?.size && <OperationHours operationHours={operationHours} />}
        </>
      )}
      <StyledContainer>
        <StyledIconDiv>
          <StyledToolTips title="Telephone" enterDelay={100} arrow>
            <PhoneEnabledIcon color="disabled" />
          </StyledToolTips>
        </StyledIconDiv>
        <div>{phone ?? '-'}</div>
      </StyledContainer>
      <StyledContainer>
        <StyledIconDiv>
          <StyledToolTips title="Fax" enterDelay={100} arrow>
            <PrintIcon color="disabled" />
          </StyledToolTips>
        </StyledIconDiv>
        <div>{fax ?? '-'}</div>
      </StyledContainer>
      <StyledContainer>
        <StyledIconDiv>
          <StyledToolTips title="Email" enterDelay={100} arrow>
            <EmailIcon color="disabled" />
          </StyledToolTips>
        </StyledIconDiv>
        <div>{email ?? '-'}</div>
      </StyledContainer>
      <StyledContainer>
        <StyledIconDiv>
          <StyledToolTips title="Website" enterDelay={100} arrow>
            <LanguageIcon color="disabled" />
          </StyledToolTips>
        </StyledIconDiv>
        {url ? (
          <StyledWebsiteIcon href={url?.includes('http') ? url : `http://${url}`} rel="noopener" target="_blank">
            {url}
          </StyledWebsiteIcon>
        ) : (
          <div>-</div>
        )}
      </StyledContainer>
      {resourceType === 'Organization' && (
        <>
          <StyledContainer>
            <StyledIconDiv>
              <StyledToolTips title="Contact Person" enterDelay={100} arrow>
                <PersonIcon color="disabled" />
              </StyledToolTips>
            </StyledIconDiv>
            <div>
              <div>{contact ?? '-'}</div>
            </div>
          </StyledContainer>
          <SuggestEdit value={value} />
        </>
      )}
      {resourceType === 'Practitioner' && (
        <>
          <h3>Work Location</h3>
          <StyledContainer>
            <StyledIconDiv>
              <StyledToolTips title="Location" enterDelay={100} arrow>
                <RoomIcon color="disabled" />
              </StyledToolTips>
            </StyledIconDiv>
            <div>
              <div>{fullAddress ?? '-'}</div>
              {searchParams.get('d') === '1' && <StyledDebugDiv>ID: {locationId}</StyledDebugDiv>}
            </div>
          </StyledContainer>
          {!!operationHours?.size && <OperationHours operationHours={operationHours} />}
          {!!locationSecondary.length &&
            locationSecondary.map((item) => (
              <StyledContainer key={item}>
                <StyledIconDiv>
                  <StyledToolTips title="Location" enterDelay={100} arrow>
                    <RoomIcon color="disabled" />
                  </StyledToolTips>
                </StyledIconDiv>
                <StyledWebsiteIcon
                  href={`https://www.google.com/maps/search/?api=1&query=${item.split(' ').join('+')}`}
                  rel="noopener"
                  target="_blank"
                >
                  {item}
                </StyledWebsiteIcon>
              </StyledContainer>
            ))}

          {!!registrationProvince?.length && (
            <>
              <h3>Registration Province</h3>
              <StyledContainer>
                <StyledIconDiv>
                  <StyledToolTips title="ID" enterDelay={100} arrow>
                    <CreditCardIcon color="disabled" />
                  </StyledToolTips>
                </StyledIconDiv>
                <StyledContainerColumn>
                  {registrationProvince?.map(({ system, value: province }) => (
                    <div key={`${province}/${system}`}>
                      {system}({province})
                    </div>
                  ))}
                </StyledContainerColumn>
              </StyledContainer>
            </>
          )}

          {!!billingNumbers?.length && (
            <>
              <h3>Billing Numbers</h3>
              <StyledContainer>
                <StyledIconDiv>
                  <StyledToolTips title="ID" enterDelay={100} arrow>
                    <CreditCardIcon color="disabled" />
                  </StyledToolTips>
                </StyledIconDiv>
                <StyledContainerColumn>
                  {billingNumbers?.map(({ system, value: province }) => (
                    <div key={`${province}/${system}`}>
                      {system}({province})
                    </div>
                  ))}
                </StyledContainerColumn>
              </StyledContainer>
            </>
          )}

          <h3>Languages Spoken</h3>
          <StyledContainer>
            <div>{language ?? '-'}</div>
          </StyledContainer>
          <h3>Sex</h3>
          <StyledContainer>
            <div>{gender ?? '-'}</div>
          </StyledContainer>
          <h3>Catchment Area</h3>
          <StyledContainer>{catchmentArea ?? '-'}</StyledContainer>
          <SuggestEdit value={value} />
        </>
      )}
    </StyledInfoContainer>
  );
};

export default PractitionerInfo;
